import { Cart } from "@omni/types/api/cart/bff/cart.model";
import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Country } from "@omni/types/enums/country.enum";

import { CreateAddress } from "@/redux/modules/api/data/address/address.types";

export const formatAddressStreetLine = (
    street: string,
    number: string,
    box?: string | null
): string => {
    return `${street} ${number}${(box && `/${box}`) || ""}`;
};

export const getPaymentAddress = (
    addresses: Array<CloudCustomerAddress> = [],
    shoppingCart?: Cart | null
) => {
    const paymentAddress = addresses.find((address) => {
        if (shoppingCart?.billingAddressId) {
            return address.id === shoppingCart.billingAddressId;
        } else {
            return address.defaultPaymentAddress;
        }
    });

    return paymentAddress || (addresses.length > 0 ? addresses[0] : undefined);
};

export const getAllowedDeliveryAddresses = (
    addresses: Array<CloudCustomerAddress>
) => addresses.filter((address) => address.country === Country.Be);

export const getDeliveryAddress = (
    addresses: Array<CloudCustomerAddress> = [],
    shoppingCart?: Cart | null
): CloudCustomerAddress | undefined => {
    const deliveryAddress = addresses.find((address) => {
        if (shoppingCart?.deliveryAddressId) {
            return address.id === shoppingCart.deliveryAddressId;
        } else {
            return address.defaultDeliveryAddress;
        }
    });

    return (
        deliveryAddress ||
        addresses.find((address) => address.country === Country.Be)
    );
};

export const getPrefixOfFIeld = (fieldName: string): string => {
    const prefix = fieldName.substring(0, fieldName.indexOf("."));
    return prefix ? `${prefix}.` : "";
};

export const getCreateAddressObject = (
    addressing: string | null | undefined,
    firstName: string,
    lastName: string,
    country: string,
    city: string,
    street: string,
    box: string | null | undefined,
    number: string,
    zipCode: string,
    validationStatus: AddressValidationStatus,
    onlyApplicableAsDeliveryAddress: boolean = false,
    onlyApplicableAsPaymentAddress: boolean = false
): CreateAddress => {
    return {
        addressing: addressing || "",
        firstName,
        lastName,
        country,
        city,
        street,
        box: box || "",
        number,
        zipCode,
        validationStatus,
        onlyApplicableAsDeliveryAddress,
        onlyApplicableAsPaymentAddress
    };
};

const getDeliveryEmptyAddress = () => {
    return {
        id: undefined,
        addressing: "",
        firstName: "",
        lastName: "",
        street: "",
        number: "",
        box: "",
        zipCode: "",
        city: "",
        country: "BE",
        address: "",
        selected: false,
        validationStatus: AddressValidationStatus.Unknown,
        isComplete: false
    };
};

const getPaymentEmptyAddress = () => {
    return {
        id: undefined,
        firstName: "",
        lastName: "",
        street: "",
        number: "",
        box: "",
        zipCode: "",
        city: "",
        country: "BE",
        address: "",
        selected: false,
        validationStatus: AddressValidationStatus.Unknown,
        isComplete: false
    };
};

type AddressTypeMap = {
    [AddressType.Delivery]: typeof getDeliveryEmptyAddress;
    [AddressType.Payment]: typeof getPaymentEmptyAddress;
    [AddressType.Default]: typeof getDeliveryEmptyAddress;
};

const AddressMap: AddressTypeMap = {
    [AddressType.Delivery]: getDeliveryEmptyAddress,
    [AddressType.Payment]: getPaymentEmptyAddress,
    [AddressType.Default]: getDeliveryEmptyAddress
};

export const getEmptyAddress = <TType extends AddressType>(
    addressType: TType
): ReturnType<AddressTypeMap[TType]> => {
    return AddressMap[addressType]() as ReturnType<AddressTypeMap[TType]>;
};
